import React, { useEffect, useState } from 'react';
import { Activity, CheckCircle, Euro, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../api';
import { formatEUR } from '../utils/format';
import LeavesTable from './LeavesTable';

const Dashboard = () => {
  const [stats, setStats] = useState({ total: 0, successes: 0, errors: 0, total_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [compliance, setCompliance] = useState(null);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await api.post('/qonto/sync');
      alert("Synchronisation Qonto lancée ! Les nouvelles factures apparaîtront d'ici quelques instants.");
    } catch (err) {
      alert("Erreur lors du lancement de la synchronisation.");
      console.error(err);
    } finally {
      setTimeout(() => setSyncing(false), 2000);
    }
  };

  const loadData = async () => {
    try {
      const statsRes = await api.get('/stats');
      const data = statsRes.data;
      const currentYear = new Date().getFullYear();
      const requiredYears = [currentYear.toString(), (currentYear - 1).toString()];

      if (!data.yearly_stats) data.yearly_stats = [];
      requiredYears.forEach(y => {
        if (!data.yearly_stats.find(item => item.year === y)) data.yearly_stats.push({ year: y, total_ht: 0, total_ttc: 0 });
      });
      data.yearly_stats = data.yearly_stats.filter(item => requiredYears.includes(item.year));
      data.yearly_stats.sort((a, b) => parseInt(b.year) - parseInt(a.year));

      if (!data.source_stats) data.source_stats = [];
      requiredYears.forEach(y => {
        if (!data.source_stats.find(item => item.year === y)) data.source_stats.push({ year: y, total: 0, qonto: 0, email: 0, manuel: 0 });
      });
      data.source_stats = data.source_stats.filter(item => requiredYears.includes(item.year));
      data.source_stats.sort((a, b) => parseInt(b.year) - parseInt(a.year));

      setStats(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, []);

  // Conformité SIRET/TVA (chargé une fois — appel Axonaut plus lourd, mis en cache côté serveur)
  useEffect(() => {
    api.get('/axonaut/siret-compliance').then(r => setCompliance(r.data)).catch(() => {});
  }, []);

  const availableProjects = React.useMemo(() => {
    if (!stats.project_expenses || !Array.isArray(stats.project_expenses)) return [];
    const projs = new Set();
    stats.project_expenses.forEach(item => {
      if (item && item.project && item.project !== 'Sans Projet' && item.project !== 'Divers') {
        projs.add(item.project);
      }
    });
    return Array.from(projs).sort();
  }, [stats.project_expenses]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', marginTop: '3rem' }}><RefreshCw className="spinning" size={32} color="var(--brand)" /></div>;

  const formatChartData = (projectExpenses) => {
    if (!projectExpenses || !Array.isArray(projectExpenses)) return { data: [], projects: [] };
    const grouped = {};
    const projects = new Set();
    
    projectExpenses.forEach(item => {
        if (!item || !item.month || !item.project || item.project === 'Sans Projet' || item.project === 'Divers') return;
        if (selectedProjects.length > 0 && !selectedProjects.includes(item.project)) return;

        if (!grouped[item.month]) grouped[item.month] = { month: String(item.month), Total: 0 };
        const val = parseFloat(item.total) || 0;
        grouped[item.month][item.project] = val;
        grouped[item.month].Total += val;
        projects.add(item.project);
    });
    
    return { 
      data: Object.values(grouped).sort((a,b) => (a.month || '').localeCompare(b.month || '')), 
      projects: Array.from(projects).sort() 
    };
  };

  const { data: chartData, projects: chartProjects } = formatChartData(stats.project_expenses);
  
  // Palette de couleurs pour les projets
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#64748b'];

  return (
    <div className="view-section">
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <button 
          onClick={triggerSync} 
          disabled={syncing}
          style={{ 
            display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--accent)', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: syncing ? 'not-allowed' : 'pointer', opacity: syncing ? 0.7 : 1, fontWeight: 600, fontSize: '1rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
          }}
        >
          <RefreshCw size={20} className={syncing ? 'spinning' : ''} />
          {syncing ? 'Synchronisation...' : 'Synchro Qonto'}
        </button>
      </div>

      {/* KPI en haut */}
      <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: 'var(--text-primary)', fontWeight: '600' }}>Statistiques Globales</h3>
      <div className="stats-grid" style={{ marginBottom: '2.5rem' }}>
        <div className="stat-card glass-panel" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
            <div className="stat-icon blue"><Activity size={24} /></div>
            <div>
              <h3 style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Factures Traitées</h3>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0' }}>{stats.source_stats ? stats.source_stats.reduce((acc, curr) => acc + curr.total, 0) : stats.total}</h2>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {stats.source_stats && stats.source_stats.map(s => (
              <div key={s.year} style={{ fontSize: '0.75rem', background: 'var(--bg-secondary)', padding: '0.5rem', borderRadius: '6px' }}>
                <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Année {s.year} ({s.total})</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)' }}>
                  <span><span style={{color: '#00e5ff'}}>■</span> Qonto: {s.qonto}</span>
                  <span><span style={{color: '#ffa200'}}>■</span> Mail: {s.email}</span>
                  <span><span style={{color: '#888'}}>■</span> Manu: {s.manuel}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="stat-card glass-panel" style={{ alignItems: 'flex-start' }}>
          <div className="stat-icon green"><CheckCircle size={24} /></div>
          <div>
            <h3 style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Succès Global</h3>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0.25rem 0' }}>{stats.successes}</h2>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Dans Axonaut (ce mois)</span>
          </div>
        </div>

        <div className="stat-card glass-panel" style={{ alignItems: 'flex-start' }}>
          <div className="stat-icon purple"><Euro size={24} /></div>
          <div>
            <h3 style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Montant Total</h3>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0.25rem 0' }}>{formatEUR(stats.total_amount) || '0,00 €'}</h2>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>TTC automatisé (ce mois)</span>
          </div>
        </div>
      </div>

      {compliance && (
        <div className="glass-panel" style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <CheckCircle size={18} color={compliance.clients.missing.length === 0 ? '#10b981' : '#f59e0b'} />
            Conformité facturation électronique
          </h3>
          {compliance.clients.missing.length === 0 ? (
            <div style={{ color: '#059669', fontWeight: 600, fontSize: '0.9rem' }}>
              ✅ {compliance.clients.total}/{compliance.clients.total} clients conformes (SIRET + N° TVA)
            </div>
          ) : (
            <div>
              <div style={{ color: '#b45309', fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                ⚠️ {compliance.clients.missing.length} client(s) à compléter sur {compliance.clients.total}
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {compliance.clients.missing.map((c, i) => (
                  <li key={i}><strong style={{ color: 'var(--text-primary)' }}>{c.name}</strong>{c.city ? ` (${c.city})` : ''} — {c.issues.join(', ')}</li>
                ))}
              </ul>
            </div>
          )}
          <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--glass-border)', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            Fournisseurs FR : {compliance.suppliers_fr.missing}/{compliance.suppliers_fr.total} sans SIRET/TVA — majoritairement des contreparties de dépenses (tickets, restos, SaaS…), non prioritaire pour la e-facturation.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '2px solid var(--glass-border)', paddingBottom: '0.5rem' }}>
        <h3 style={{ fontSize: '1.25rem', color: 'var(--text-primary)', fontWeight: '700', margin: 0 }}>
          Dépenses par Projet (Année en cours)
        </h3>
      </div>
      
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button 
          onClick={() => setSelectedProjects([])}
          style={{ padding: '6px 14px', borderRadius: '16px', fontSize: '0.8rem', cursor: 'pointer', border: '1px solid var(--glass-border)', background: selectedProjects.length === 0 ? 'var(--accent)' : 'var(--bg-secondary)', color: selectedProjects.length === 0 ? 'white' : 'var(--text-secondary)', fontWeight: 600, transition: 'all 0.2s' }}
        >
          Tous les projets
        </button>
        {availableProjects.map((proj, idx) => {
          const isSelected = selectedProjects.includes(proj);
          const color = COLORS[idx % COLORS.length];
          return (
            <button 
              key={proj}
              onClick={() => {
                if (isSelected) {
                  setSelectedProjects(selectedProjects.filter(p => p !== proj));
                } else {
                  setSelectedProjects([...selectedProjects, proj]);
                }
              }}
              style={{ 
                padding: '6px 14px', borderRadius: '16px', fontSize: '0.8rem', cursor: 'pointer', border: `1px solid ${isSelected ? color : 'var(--glass-border)'}`, 
                background: isSelected ? color : 'var(--bg-secondary)', 
                color: isSelected ? 'white' : 'var(--text-secondary)',
                fontWeight: isSelected ? 600 : 500,
                transition: 'all 0.2s'
              }}
            >
              {proj}
            </button>
          );
        })}
      </div>

      <div className="glass-panel" style={{ height: '400px', padding: '1.5rem', marginBottom: '2.5rem' }}>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <pattern id="totalPattern" patternUnits="userSpaceOnUse" width="8" height="8">
                  <rect width="8" height="8" fill="#1e293b" />
                  <path d="M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4" stroke="#475569" strokeWidth="2" />
                </pattern>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--glass-border)" />
              <XAxis dataKey="month" tick={{fill: 'var(--text-secondary)'}} axisLine={{stroke: 'var(--glass-border)'}} />
              <YAxis tick={{fill: 'var(--text-secondary)'}} axisLine={{stroke: 'var(--glass-border)'}} tickFormatter={(value) => `${value}€`} />
              <Tooltip 
                contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--glass-border)', borderRadius: '8px' }}
                itemStyle={{ color: 'var(--text-primary)' }}
                formatter={(value) => formatEUR(value)}
              />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              {chartProjects.map((proj) => {
                const colorIdx = availableProjects.indexOf(proj);
                const safeColorIdx = colorIdx >= 0 ? colorIdx : 0;
                return <Bar key={proj} dataKey={proj} fill={COLORS[safeColorIdx % COLORS.length]} radius={[4, 4, 0, 0]} />;
              })}
              <Bar key="Total" dataKey="Total" fill="url(#totalPattern)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'var(--text-secondary)' }}>
            Aucune dépense pour l'année en cours.
          </div>
        )}
      </div>

      <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: 'var(--text-primary)', fontWeight: '700', borderBottom: '2px solid var(--glass-border)', paddingBottom: '0.5rem' }}>
        Tableau des Congés
      </h3>
      <LeavesTable />
    </div>
  );
};

export default Dashboard;
